import React, { useEffect, useState } from "react";
import { Loader2, X, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { money } from "./wsUtils";
import { SourceTag } from "./SourceTag";

const fmtVal = (k, v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length ? v.map((x) => { const [n, a] = String(x).split("|"); return `${n} ${a && a !== "None" ? money(Number(a)) : ""}`; }).join("；") : "—";
  if (/Cents$/.test(k)) return money(Number(v));
  return String(v);
};

/** 受控重新带入: 选报价 → 逐字段差异 (标注手改) → 勾选覆盖 */
export function ReimportDialog({ api, leadId, data, zh, onClose, onApplied }) {
  const ests = data.estimates || [];
  const [estId, setEstId] = useState(data.estimateId || ests[0]?.id || "");
  const [diffs, setDiffs] = useState(null);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const run = async (id) => {
    setBusy(true); setDiffs(null);
    try {
      const { data: r } = await api.post(`/crm/leads/${leadId}/contract/workspace/reimport`, { estimateId: id || undefined, dryRun: true });
      setDiffs(r.diffs || []);
      setSel(Object.fromEntries((r.diffs || []).map((d) => [d.field, !d.manual])));
    } catch (e) { toast.error(e?.detailText || (zh ? "对比失败" : "Failed")); setDiffs([]); }
    setBusy(false);
  };
  useEffect(() => { run(estId); }, [estId]); // eslint-disable-line
  const apply = async () => {
    const keys = Object.keys(sel).filter((k) => sel[k]);
    if (!keys.length) { toast.warning(zh ? "未选择要覆盖的字段" : "Nothing selected"); return; }
    setBusy(true);
    try {
      await api.post(`/crm/leads/${leadId}/contract/workspace/reimport`, { estimateId: estId || undefined, apply: true, fields: keys });
      toast.success(zh ? `已重新带入 ${keys.length} 项` : `Re-imported ${keys.length} field(s)`);
      onApplied();
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "带入失败" : "Failed")); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[95] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ws-reimport-dialog" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div><h3 className="font-bold text-slate-800">{zh ? "重新带入 · 逐字段对比" : "Re-import · field-by-field diff"}</h3><p className="text-[11.5px] text-slate-500">{zh ? "只覆盖勾选的字段；销售手改字段默认不覆盖。" : "Only checked fields are overwritten; manual edits are unchecked by default."}</p></div>
          <button data-testid="ws-reimport-close" onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-5 py-3 border-b border-slate-100">
          <label className="text-[12px] text-slate-600 block mb-1">{zh ? "报价版本" : "Estimate version"}</label>
          <select data-testid="ws-reimport-estimate" value={estId} onChange={(e) => setEstId(e.target.value)} className="w-full text-sm rounded-lg border border-slate-200 px-2.5 py-1.5 bg-white focus:outline-none focus:border-brand-400">
            {ests.map((e) => <option key={e.id} value={e.id} disabled={e.needsApproval && !e.priceApproved}>{e.id} · {money(e.totalCents)} · {e.tierName || ""} · {String(e.createdAt || "").slice(0, 10)}{e.needsApproval && !e.priceApproved ? (zh ? "（改价待审批）" : " (pending approval)") : ""}{e.id === data.estimateId ? (zh ? "（当前）" : " (current)") : ""}</option>)}
            <option value="">{zh ? "仅客户资料 / 踏勘（不带报价）" : "Lead / survey only (no estimate)"}</option>
          </select>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {busy && !diffs && <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />{zh ? "对比中…" : "Comparing…"}</div>}
          {diffs && diffs.length === 0 && <p data-testid="ws-reimport-nodiff" className="text-center text-[13px] text-emerald-700 py-8">{zh ? "当前草稿与该来源无差异" : "No differences"}</p>}
          {diffs && diffs.length > 0 && (
            <table className="w-full text-[12px]" data-testid="ws-reimport-diffs">
              <thead><tr className="text-left text-slate-400"><th className="py-1 w-8"></th><th className="py-1">{zh ? "字段" : "Field"}</th><th className="py-1">{zh ? "当前" : "Current"}</th><th className="py-1 w-6"></th><th className="py-1">{zh ? "带入后" : "Incoming"}</th></tr></thead>
              <tbody>
                {diffs.map((d) => (
                  <tr key={d.field} data-testid={`ws-diff-${d.field}`} data-manual={d.manual ? 1 : 0} className="border-t border-slate-100 align-top">
                    <td className="py-1.5"><input type="checkbox" data-testid={`ws-diff-check-${d.field}`} checked={!!sel[d.field]} onChange={(e) => setSel({ ...sel, [d.field]: e.target.checked })} /></td>
                    <td className="py-1.5"><div className="font-medium text-slate-700">{zh ? d.labelZh : d.labelEn}</div><SourceTag src={d.currentSrc || "pending"} labels={data.srcLabels} zh={zh} testid={`ws-diff-src-${d.field}`} />{d.manual && <span className="ml-1 text-[10px] text-amber-700">{zh ? "销售手改" : "manual edit"}</span>}</td>
                    <td className="py-1.5 text-slate-600 break-all">{fmtVal(d.field, d.current)}</td>
                    <td className="py-1.5 text-slate-300"><ArrowRight className="h-3.5 w-3.5" /></td>
                    <td className="py-1.5 text-violet-700 font-medium break-all">{fmtVal(d.field, d.incoming)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center gap-2 justify-end">
          <Button variant="outline" data-testid="ws-reimport-cancel" onClick={onClose}>{zh ? "取消" : "Cancel"}</Button>
          <Button data-testid="ws-reimport-apply" onClick={apply} disabled={busy || !diffs || diffs.length === 0} className="bg-violet-600 hover:bg-violet-700 text-white">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}{zh ? `覆盖勾选项（${Object.values(sel).filter(Boolean).length}）` : `Apply selected (${Object.values(sel).filter(Boolean).length})`}</Button>
        </div>
      </div>
    </div>
  );
}
